import { useState } from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  Pagination,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { useFieldSalesBreakdown } from '../hooks/useFieldSalesDashboard.js';
import {
  formatValueAuto,
  formatIndianInt,
  formatPct,
  achDelta,
  achBar,
  deltaClass,
  DELTA_COLOR,
  FS_HEAD_SX,
  FS_CELL_SX,
} from './fieldSalesFormat.js';

const NAVY = '#0F1E3D';
const MUTED = '#6B7280';
const BORDER = '#E4E8EE';
const GOOD = '#16A34A';
const PRIMARY = '#2E7CF6';
const BAD = '#DC2626';

// Dimensions the backend supports. Brand is the default. `dim` is the wire value.
const DIMS = [
  { dim: 'division', label: 'Division' },
  { dim: 'zone', label: 'Zone' },
  { dim: 'state', label: 'State' },
  { dim: 'hq', label: 'HQ' },
  { dim: 'brand', label: 'Brand' },
  { dim: 'sku', label: 'SKU' },
  { dim: 'bdm', label: 'BDM' },
  { dim: 'zm', label: 'ZM' },
  { dim: 'rm', label: 'RM' },
];

// Dimensions with no data source yet — shown AFTER the live ones, greyed out
// (50% opacity, not clickable, "No data source yet" tooltip, "No data" tag).
const DISABLED_DIMS = [{ label: 'Category' }];

const NO_DATA_TOOLTIP = 'No data source yet';

const ACH_BAR_FILL = { good: GOOD, mid: PRIMARY, bad: BAD };

/** Dimension pill (reference #paDimSwitcher label). */
function DimPill({ label, active, onClick }) {
  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      sx={{
        px: '15px',
        py: '6px',
        borderRadius: '999px',
        cursor: 'pointer',
        fontSize: '12.5px',
        fontWeight: 600,
        font: 'inherit',
        border: '1.5px solid',
        transition: 'all .12s',
        ...(active
          ? {
              background: 'linear-gradient(135deg,#2E7CF6,#1657c8)',
              color: '#fff',
              borderColor: '#1657c8',
              boxShadow: '0 3px 9px rgba(46,124,246,.45)',
            }
          : {
              bgcolor: '#eef4ff',
              color: '#2554a8',
              borderColor: '#bcd5fb',
              '&:hover': { bgcolor: '#dbe8ff', borderColor: '#8fbaf6' },
            }),
      }}
    >
      {label}
    </Box>
  );
}

/**
 * Greyed dimension pill for a dim with no data source yet: 50% opacity, not
 * clickable, "No data source yet" tooltip, and a small "No data" tag.
 */
function DisabledDimPill({ label }) {
  return (
    <Tooltip title={NO_DATA_TOOLTIP} arrow>
      <Box
        component="span"
        aria-disabled="true"
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          px: '15px',
          py: '6px',
          borderRadius: '999px',
          fontSize: '12.5px',
          fontWeight: 600,
          border: '1.5px solid #bcd5fb',
          bgcolor: '#eef4ff',
          color: '#2554a8',
          opacity: 0.5,
          cursor: 'not-allowed',
          pointerEvents: 'auto', // keep the tooltip while blocking clicks
          userSelect: 'none',
        }}
      >
        {label}
        <Box
          component="span"
          sx={{
            ml: '6px',
            px: '6px',
            py: '1px',
            borderRadius: '999px',
            bgcolor: '#eef1f5',
            border: `1px solid ${BORDER}`,
            color: MUTED,
            fontSize: '9px',
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '.3px',
            whiteSpace: 'nowrap',
          }}
        >
          No data
        </Box>
      </Box>
    </Tooltip>
  );
}

/** Blue-gradient summary card (reference #paSummary .mini). */
function BlueCard({ label, value, sub, valueColor }) {
  return (
    <Box
      sx={{
        background: 'linear-gradient(135deg,#2E7CF6,#1B4FBF)',
        borderRadius: '14px',
        p: '15px 18px',
        boxShadow: '0 6px 18px rgba(27,79,191,.30)',
      }}
    >
      <Typography sx={{ color: '#dbe7ff', fontWeight: 700, letterSpacing: '.5px', fontSize: '11px', textTransform: 'uppercase' }}>
        {label}
      </Typography>
      <Typography sx={{ color: valueColor || '#fff', fontSize: '26px', fontWeight: 700, lineHeight: 1.15, mt: '2px' }}>
        {value}
      </Typography>
      {sub ? <Typography sx={{ color: '#c3d6f8', fontSize: '11px', mt: '2px' }}>{sub}</Typography> : null}
    </Box>
  );
}

const HEAD_SX = FS_HEAD_SX;
const CELL_SX = FS_CELL_SX;

/** Achievement cell: a small bar + the % in its tone colour (reference ach-bar). */
function AchCell({ pct }) {
  if (!Number.isFinite(Number(pct))) return <>—</>;
  const p = Number(pct);
  const w = Math.min(120, Math.max(0, p)) / 1.2; // 0..100 width, capped at 120%
  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
      <Box
        sx={{
          display: 'inline-block',
          width: 46,
          height: 7,
          bgcolor: '#eef1f5',
          borderRadius: '999px',
          overflow: 'hidden',
          mr: '6px',
        }}
      >
        <Box sx={{ display: 'block', height: '100%', borderRadius: '999px', width: `${w}%`, bgcolor: ACH_BAR_FILL[achBar(p)] }} />
      </Box>
      <Box component="span" sx={{ color: DELTA_COLOR[achDelta(p)] }}>
        {p.toFixed(1)}%
      </Box>
    </Box>
  );
}

/** Signed growth % in its delta colour. */
function GrowthCell({ pct }) {
  if (!Number.isFinite(Number(pct))) return <>—</>;
  const p = Number(pct);
  return (
    <Box component="span" sx={{ color: DELTA_COLOR[deltaClass(p)] }}>
      {(p > 0 ? '+' : '') + p.toFixed(1)}%
    </Box>
  );
}

/** ₹ Cr/L (lakh input), em dash for non-positive. */
const money = (v) => (Number(v) > 0 ? formatValueAuto(v) : '—');

/**
 * Resolve a breakdown row's Division value. Reads `row.division` (with a couple
 * of alternate key casings the backend may use) and returns the trimmed value
 * when present — only falling back to the em dash when there is genuinely no
 * value, so a real division is never masked by "—".
 */
function divisionOf(row) {
  const raw = row?.division ?? row?.Division ?? row?.div;
  const str = raw == null ? '' : String(raw).trim();
  return str || '—';
}

/**
 * Field Sales — "{Mon} — Target Achievement & YoY Growth" section (reference
 * Period Analysis). Dimension pills, four blue summary cards (from the API
 * TOTAL row) and the breakdown table with the reference columns + a TOTAL row,
 * paged by the API's page/page_size.
 *
 * @param {{ pl: string }} props  pl = the resolved {Mon} token
 */
export default function FieldSalesPerformanceAnalysis({ pl }) {
  const [dim, setDim] = useState('brand'); // Brand is the default
  const [page, setPage] = useState(1);

  const query = useFieldSalesBreakdown({ dim, page });
  const data = query.data;
  const rows = data?.rows ?? [];
  const totalRow = data?.totalRow ?? {};
  const pageSize = data?.pageSize || 50;
  const total = data?.total || 0;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const curPage = data?.page ?? page;

  const dimLabel = DIMS.find((d) => d.dim === dim)?.label ?? dim;
  // The Division column is the first column for every dimension EXCEPT Division
  // itself (which has no Division column of its own). Zone/State/HQ/Brand/SKU
  // all show it.
  const showDivisionCol = dim !== 'division';

  const selectDim = (next) => {
    if (next === dim) return;
    setDim(next);
    setPage(1); // reset to first page on dimension change
  };

  // Column count for the TOTAL label colspan (Division col + dim col).
  const totalLabelSpan = showDivisionCol ? 2 : 1;

  return (
    <Box
      sx={{
        bgcolor: '#fff',
        border: `1px solid ${BORDER}`,
        borderRadius: '12px',
        boxShadow: '0 1px 2px rgba(15,30,61,.04), 0 4px 18px rgba(15,30,61,.06)',
        p: '16px 18px',
        mb: '16px',
      }}
    >
      {/* Heading + dimension pills */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '12px',
          flexWrap: 'wrap',
          mb: '12px',
        }}
      >
        <Typography sx={{ fontSize: '15px', fontWeight: 700, color: NAVY }}>
          🎯 {pl} — Target Achievement &amp; YoY Growth
        </Typography>
        <Box sx={{ display: 'flex', gap: '7px', flexWrap: 'wrap' }}>
          {DIMS.map((d) => (
            <DimPill key={d.dim} label={d.label} active={d.dim === dim} onClick={() => selectDim(d.dim)} />
          ))}
          {/* No-data-source dims, greyed, shown after the live ones. */}
          {DISABLED_DIMS.map((d) => (
            <DisabledDimPill key={d.label} label={d.label} />
          ))}
        </Box>
      </Box>

      {/* Four blue summary cards (from the TOTAL row) */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2,1fr)', md: 'repeat(4,1fr)' },
          gap: '14px',
          mb: '12px',
        }}
      >
        <BlueCard label={`${pl} Sales`} value={money(totalRow.sales)} />
        <BlueCard label={`${pl} Target`} value={money(totalRow.target)} />
        <BlueCard
          label="Achievement %"
          value={Number.isFinite(Number(totalRow.ach)) ? `${Number(totalRow.ach).toFixed(1)}%` : '—'}
          sub={`vs ${pl} target`}
        />
        <BlueCard
          label="Growth % (YoY)"
          value={formatPct(totalRow.growth, true)}
          sub={Number(totalRow.ly) > 0 ? `${pl} LY: ${formatValueAuto(totalRow.ly)}` : 'no LY rows'}
        />
      </Box>

      {/* Table */}
      {query.isError ? (
        <Alert severity="error">Failed to load the {dimLabel} breakdown.</Alert>
      ) : query.isLoading || query.isFetching ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={28} />
        </Box>
      ) : rows.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
          No {dimLabel} rows for the current filters.
        </Typography>
      ) : (
        <>
          <TableContainer sx={{ maxHeight: 520, border: `1px solid ${BORDER}`, borderRadius: '10px' }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  {showDivisionCol ? <TableCell sx={HEAD_SX}>Division</TableCell> : null}
                  <TableCell sx={HEAD_SX}>{dimLabel}</TableCell>
                  <TableCell sx={HEAD_SX} align="right">No. of TM</TableCell>
                  <TableCell sx={HEAD_SX} align="right">{pl} Sales (Pri)</TableCell>
                  <TableCell sx={HEAD_SX} align="right">{pl} Target</TableCell>
                  <TableCell sx={HEAD_SX} align="right">Ach %</TableCell>
                  <TableCell sx={HEAD_SX} align="right">{pl} LY</TableCell>
                  <TableCell sx={HEAD_SX} align="right">Growth %</TableCell>
                  <TableCell sx={HEAD_SX} align="right">PCPM/TM</TableCell>
                  <TableCell sx={HEAD_SX} align="right">{pl} SEC</TableCell>
                  <TableCell sx={HEAD_SX} align="right">Sec vs Pri %</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((r, i) => (
                  <TableRow key={`${divisionOf(r)}-${r.name}-${i}`} hover>
                    {showDivisionCol ? <TableCell sx={CELL_SX}>{divisionOf(r)}</TableCell> : null}
                    <TableCell sx={{ ...CELL_SX, fontWeight: 600 }}>{r.name ?? '—'}</TableCell>
                    <TableCell sx={CELL_SX} align="right">{formatIndianInt(r.tm)}</TableCell>
                    <TableCell sx={CELL_SX} align="right">{money(r.sales)}</TableCell>
                    <TableCell sx={CELL_SX} align="right">{money(r.target)}</TableCell>
                    <TableCell sx={CELL_SX} align="right"><AchCell pct={r.ach} /></TableCell>
                    <TableCell sx={CELL_SX} align="right">{money(r.ly)}</TableCell>
                    <TableCell sx={CELL_SX} align="right"><GrowthCell pct={r.growth} /></TableCell>
                    <TableCell sx={CELL_SX} align="right">
                      {Number.isFinite(Number(r.pcpm_tm)) ? formatValueAuto(r.pcpm_tm) : '—'}
                    </TableCell>
                    <TableCell sx={CELL_SX} align="right">{money(r.secondary)}</TableCell>
                    <TableCell sx={CELL_SX} align="right">
                      {Number.isFinite(Number(r.sec_vs_pri_pct)) ? `${Number(r.sec_vs_pri_pct).toFixed(0)}%` : '—'}
                    </TableCell>
                  </TableRow>
                ))}
                {Object.keys(totalRow).length ? (
                  <TableRow sx={{ '& td': { bgcolor: '#f8fafc', fontWeight: 700, borderTop: `2px solid ${BORDER}` } }}>
                    <TableCell sx={CELL_SX} colSpan={totalLabelSpan}>Total</TableCell>
                    <TableCell sx={CELL_SX} align="right">{formatIndianInt(totalRow.tm)}</TableCell>
                    <TableCell sx={CELL_SX} align="right">{money(totalRow.sales)}</TableCell>
                    <TableCell sx={CELL_SX} align="right">{money(totalRow.target)}</TableCell>
                    <TableCell sx={CELL_SX} align="right">
                      <Box component="span" sx={{ color: DELTA_COLOR[achDelta(totalRow.ach)] }}>
                        {Number.isFinite(Number(totalRow.ach)) ? `${Number(totalRow.ach).toFixed(1)}%` : '—'}
                      </Box>
                    </TableCell>
                    <TableCell sx={CELL_SX} align="right">{money(totalRow.ly)}</TableCell>
                    <TableCell sx={CELL_SX} align="right"><GrowthCell pct={totalRow.growth} /></TableCell>
                    <TableCell sx={CELL_SX} align="right">
                      {Number.isFinite(Number(totalRow.pcpm_tm)) ? formatValueAuto(totalRow.pcpm_tm) : '—'}
                    </TableCell>
                    <TableCell sx={CELL_SX} align="right">{money(totalRow.secondary)}</TableCell>
                    <TableCell sx={CELL_SX} align="right">
                      {Number.isFinite(Number(totalRow.sec_vs_pri_pct)) ? `${Number(totalRow.sec_vs_pri_pct).toFixed(0)}%` : '—'}
                    </TableCell>
                  </TableRow>
                ) : null}
              </TableBody>
            </Table>
          </TableContainer>

          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1.5, flexWrap: 'wrap', gap: 1 }}>
            <Typography sx={{ fontSize: '12px', color: MUTED }}>
              {total} {dimLabel.toLowerCase()} rows · page {curPage} of {pageCount}
            </Typography>
            <Pagination
              count={pageCount}
              page={curPage}
              onChange={(_event, value) => setPage(value)}
              size="small"
              color="primary"
            />
          </Box>
        </>
      )}
    </Box>
  );
}
